import { useState, useEffect, useContext } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { MdArrowBack, MdPerson, MdGroup, MdSearch } from 'react-icons/md'
import { HiSparkles } from 'react-icons/hi2'
import { SocketContext } from '@/lib/providers/socket-provider'
import { RootState, AppDispatch } from '@/store'
import {
    setConversations,
    setCurrentConversation,
    setMessages,
    addMessage,
    setUnreadCount,
    setLoading,
    addTypingUser,
    removeTypingUser,
    setReplyingTo,
    updateConversationLastMessage,
    incrementConversationUnread,
    resetConversationUnread,
    updateMessageStatus,
} from '@/store/slices/internalChatSlice'
import {
    getConversations,
    getConversation,
    getMessages,
    createConversation,
    sendMessage,
    markAsRead,
    getUnreadCount,
    getAvailableUsers,
    InternalMessage,
    InternalChatUser,
    InternalConversationType,
} from '@/lib/api/internal-chat/internal-chat-api'
import ConversationList from './components/ConversationList'
import ChatWindow from './components/ChatWindow'
import MessageInput from './components/MessageInput'

type TabType = 'conversations' | 'all' | 'ai'

export default function InternalChatPage() {
    const { conversationId } = useParams<{ conversationId?: string }>()
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const dispatch = useDispatch<AppDispatch>()
    const { socket, isConnected } = useContext(SocketContext)

    const initialTab = (searchParams.get('tab') as TabType) || 'conversations'
    const [activeTab, setActiveTab] = useState<TabType>(initialTab)
    const [mobileShowChat, setMobileShowChat] = useState(false)
    const [loadingMessages, setLoadingMessages] = useState(false)
    const [users, setUsers] = useState<InternalChatUser[]>([])
    const [searchQuery, setSearchQuery] = useState('')
    const [showUserList, setShowUserList] = useState(false)

    const {
        conversations,
        currentConversation,
        messages,
        replyingTo,
        typingUsers,
        loading,
    } = useSelector((state: RootState) => state.internalChat)

    const user = useSelector((state: RootState) => state.auth.user)

    // Check if user can use AI chat
    const canUseAi =
        user?.userType === 'COMPANY' || user?.userType === 'PROFESSIONAL'

    // Load conversations on mount
    useEffect(() => {
        loadConversations()
        loadUnreadCount()
        loadUsers()
    }, [])

    // Handle URL param for conversation
    useEffect(() => {
        if (conversationId && activeTab === 'conversations') {
            loadConversationAndMessages(parseInt(conversationId))
            setMobileShowChat(true)
        }
    }, [conversationId, activeTab])

    // Handle tab change - load broadcast/AI conversation
    useEffect(() => {
        if (activeTab === 'all') {
            loadOrCreateBroadcastAll()
        } else if (activeTab === 'ai') {
            loadOrCreateAiChat()
        }
    }, [activeTab])

    // Socket event handlers
    useEffect(() => {
        if (!socket || !isConnected) return

        if (currentConversation) {
            socket.emit('internal-chat:join', {
                conversationId: currentConversation.id,
            })
        }

        const handleNewMessage = (data: {
            conversationId: number
            message: InternalMessage
        }) => {
            if (
                currentConversation &&
                data.conversationId === currentConversation.id
            ) {
                dispatch(addMessage(data.message))

                if (
                    !data.message.isAiMessage &&
                    data.message.senderId !== user?.id
                ) {
                    markAsRead(data.conversationId)
                }
            } else {
                dispatch(incrementConversationUnread(data.conversationId))
            }

            dispatch(
                updateConversationLastMessage({
                    id: data.conversationId,
                    preview:
                        data.message.content.length > 100
                            ? data.message.content.substring(0, 100) + '...'
                            : data.message.content,
                    timestamp: data.message.createdAt,
                })
            )
        }

        const handleTyping = (data: {
            conversationId: number
            userId: number
            userName: string
            isTyping: boolean
        }) => {
            if (data.isTyping) {
                dispatch(
                    addTypingUser({
                        conversationId: data.conversationId,
                        userId: data.userId,
                        userName: data.userName,
                    })
                )
            } else {
                dispatch(
                    removeTypingUser({
                        conversationId: data.conversationId,
                        userId: data.userId,
                    })
                )
            }
        }

        const handleUnreadCount = (data: { count: number }) => {
            dispatch(setUnreadCount(data.count))
        }

        const handleMessageStatus = (data: {
            conversationId: number
            messageId: number
            status: string
        }) => {
            if (
                currentConversation &&
                data.conversationId === currentConversation.id
            ) {
                dispatch(
                    updateMessageStatus({
                        messageId: data.messageId,
                        status: data.status,
                    })
                )
            }
        }

        socket.on('internal-chat:new-message', handleNewMessage)
        socket.on('internal-chat:user-typing', handleTyping)
        socket.on('internal-chat:unread-count', handleUnreadCount)
        socket.on('internal-chat:message-status', handleMessageStatus)

        return () => {
            if (currentConversation) {
                socket.emit('internal-chat:leave', {
                    conversationId: currentConversation.id,
                })
            }

            socket.off('internal-chat:new-message', handleNewMessage)
            socket.off('internal-chat:user-typing', handleTyping)
            socket.off('internal-chat:unread-count', handleUnreadCount)
            socket.off('internal-chat:message-status', handleMessageStatus)
        }
    }, [socket, isConnected, currentConversation, user?.id])

    const loadConversations = async () => {
        dispatch(setLoading(true))
        try {
            const response = await getConversations()
            if (response.success && response.result) {
                dispatch(setConversations(response.result.conversations))
            }
        } finally {
            dispatch(setLoading(false))
        }
    }

    const loadUnreadCount = async () => {
        const response = await getUnreadCount()
        if (response.success && response.result) {
            dispatch(setUnreadCount(response.result.count))
        }
    }

    const loadUsers = async () => {
        const response = await getAvailableUsers()
        if (response.success && response.result) {
            setUsers(response.result as InternalChatUser[])
        }
    }

    const loadConversationAndMessages = async (id: number) => {
        setLoadingMessages(true)
        try {
            const convResponse = await getConversation(id)
            if (convResponse.success && convResponse.result) {
                dispatch(setCurrentConversation(convResponse.result))
            }

            const msgResponse = await getMessages(id)
            if (msgResponse.success && msgResponse.result) {
                dispatch(setMessages(msgResponse.result.messages))
            }

            await markAsRead(id)
            dispatch(resetConversationUnread(id))
        } finally {
            setLoadingMessages(false)
        }
    }

    const loadOrCreateBroadcastAll = async () => {
        setLoadingMessages(true)
        try {
            const response = await createConversation({
                type: 'BROADCAST_ALL' as InternalConversationType,
            })

            if (response.success && response.result) {
                dispatch(setCurrentConversation(response.result))

                const msgResponse = await getMessages(response.result.id)
                if (msgResponse.success && msgResponse.result) {
                    dispatch(setMessages(msgResponse.result.messages))
                }

                await markAsRead(response.result.id)
                dispatch(resetConversationUnread(response.result.id))
            } else {
                toast.error(response.error || 'Erro ao carregar chat geral')
            }
        } finally {
            setLoadingMessages(false)
        }
    }

    const loadOrCreateAiChat = async () => {
        if (!canUseAi) return

        setLoadingMessages(true)
        try {
            const response = await createConversation({
                type: 'AI_CHAT' as InternalConversationType,
            })

            if (response.success && response.result) {
                dispatch(setCurrentConversation(response.result))

                const msgResponse = await getMessages(response.result.id)
                if (msgResponse.success && msgResponse.result) {
                    dispatch(setMessages(msgResponse.result.messages))
                }
            } else {
                toast.error(response.error || 'Erro ao carregar chat IA')
            }
        } finally {
            setLoadingMessages(false)
        }
    }

    const handleSendMessage = async (
        content: string,
        replyToId?: number,
        attachments?: string[]
    ) => {
        if (!currentConversation) return

        const response = await sendMessage(currentConversation.id, {
            content,
            replyToId,
            attachments,
        })

        if (response.success && response.result) {
            dispatch(addMessage(response.result))
            dispatch(setReplyingTo(null))
        } else {
            toast.error(response.error || 'Erro ao enviar mensagem')
        }
    }

    const handleBack = () => {
        if (mobileShowChat && window.innerWidth < 768) {
            setMobileShowChat(false)
            navigate('/core/internal-chat')
        } else {
            navigate(-1)
        }
    }

    const handleSelectConversation = (id: number) => {
        setActiveTab('conversations')
        navigate(`/core/internal-chat/${id}`)
    }

    const handleReply = (message: InternalMessage) => {
        dispatch(setReplyingTo(message))
    }

    const handleCancelReply = () => {
        dispatch(setReplyingTo(null))
    }

    const handleStartPrivateChat = async (userId: number) => {
        setLoadingMessages(true)
        try {
            const response = await createConversation({
                type: 'PRIVATE' as InternalConversationType,
                targetUserId: userId,
            })

            if (response.success && response.result) {
                await loadConversations()
                navigate(`/core/internal-chat/${response.result.id}`)
            } else {
                toast.error(response.error || 'Erro ao criar conversa')
            }
        } finally {
            setLoadingMessages(false)
            setShowUserList(false)
            setSearchQuery('')
        }
    }

    const handleTabChange = (tab: TabType) => {
        dispatch(setCurrentConversation(null))
        dispatch(setMessages([]))
        setActiveTab(tab)

        // Clear URL when switching away from conversations
        if (tab !== 'conversations' && conversationId) {
            navigate('/core/internal-chat', { replace: true })
        }
    }

    const filteredUsers = users.filter(
        u =>
            u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            u.email?.toLowerCase().includes(searchQuery.toLowerCase())
    )

    const getUserTypeLabel = (type: string) => {
        switch (type) {
            case 'COMPANY':
                return 'Admin'
            case 'PROFESSIONAL':
                return 'Profissional'
            case 'EMPLOYEE':
                return 'Funcionário'
            default:
                return type
        }
    }

    // Tab button component for consistent styling
    const TabButton = ({
        tab,
        icon,
        label,
        color,
    }: {
        tab: TabType
        icon: React.ReactNode
        label: string
        color: string
    }) => {
        const isActive = activeTab === tab
        return (
            <button
                type="button"
                className={`
                    flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all
                    ${
                        isActive
                            ? `bg-${color} text-${color}-content shadow-md`
                            : 'bg-base-200 text-base-content/70 hover:bg-base-300'
                    }
                `}
                onClick={() => handleTabChange(tab)}
            >
                {icon}
                <span className="hidden sm:inline">{label}</span>
            </button>
        )
    }

    return (
        <div className="flex flex-col h-[calc(100vh-10rem)] max-h-[calc(100vh-5rem)] border border-base-300 rounded-md">
            {/* Header */}
            <div className="bg-base-100 px-4 py-3 border-b border-base-300 flex items-center gap-4">
                <button
                    type="button"
                    className="btn btn-ghost btn-sm btn-square"
                    onClick={handleBack}
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <h1 className="text-lg font-bold">Chat Interno</h1>
            </div>

            {/* Tabs - Improved styling */}
            <div className="bg-base-100 border-b border-base-300 px-4 py-2">
                <div className="flex gap-2">
                    <button
                        type="button"
                        className={`
                            flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all
                            ${
                                activeTab === 'conversations'
                                    ? 'bg-secondary text-primary-content shadow-md'
                                    : 'bg-base-200 text-base-content/70 hover:bg-base-300'
                            }
                        `}
                        onClick={() => handleTabChange('conversations')}
                    >
                        <MdPerson className="text-lg" />
                        <span className="hidden sm:inline">Conversas</span>
                    </button>
                    <button
                        type="button"
                        className={`
                            flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all
                            ${
                                activeTab === 'all'
                                    ? 'bg-success text-success-content shadow-md'
                                    : 'bg-base-200 text-base-content/70 hover:bg-base-300'
                            }
                        `}
                        onClick={() => handleTabChange('all')}
                    >
                        <MdGroup className="text-lg" />
                        <span className="hidden sm:inline">Todos</span>
                    </button>
                    {/*canUseAi && (
                        <button
                            type="button"
                            className={`
                                flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all
                                ${activeTab === 'ai'
                                    ? 'bg-secondary text-secondary-content shadow-md'
                                    : 'bg-base-200 text-base-content/70 hover:bg-base-300'
                                }
                            `}
                            onClick={() => handleTabChange('ai')}
                        >
                            <HiSparkles className="text-lg" />
                            <span className="hidden sm:inline">IA</span>
                        </button>
                    )*/}
                </div>
            </div>

            {/* Main content */}
            <div className="flex-1 flex overflow-hidden">
                {/* Conversations tab - show list and chat side by side */}
                {activeTab === 'conversations' && (
                    <>
                        {/* Conversation list */}
                        <div
                            className={`w-full md:w-80 lg:w-96 border-r border-base-300 bg-base-100 flex flex-col ${
                                mobileShowChat ? 'hidden md:flex' : ''
                            }`}
                        >
                            {/* Search/New chat */}
                            <div className="p-3 border-b border-base-300">
                                {showUserList ? (
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="text"
                                                className="input input-bordered input-sm flex-1"
                                                placeholder="Buscar usuário..."
                                                value={searchQuery}
                                                onChange={e =>
                                                    setSearchQuery(
                                                        e.target.value
                                                    )
                                                }
                                                autoFocus
                                            />
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-sm"
                                                onClick={() => {
                                                    setShowUserList(false)
                                                    setSearchQuery('')
                                                }}
                                            >
                                                Cancelar
                                            </button>
                                        </div>
                                        <div className="max-h-60 overflow-y-auto border border-base-300 rounded-lg">
                                            {filteredUsers.length === 0 ? (
                                                <div className="py-4 text-center text-base-content/50 text-sm">
                                                    Nenhum usuário encontrado
                                                </div>
                                            ) : (
                                                filteredUsers.map(u => (
                                                    <button
                                                        key={u.id}
                                                        type="button"
                                                        className="w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-base-200"
                                                        onClick={() =>
                                                            handleStartPrivateChat(
                                                                u.id
                                                            )
                                                        }
                                                    >
                                                        <div className="bg-secondary/20 text-secondary rounded-full w-8 h-8 flex items-center justify-center">
                                                            {u.picture ? (
                                                                <img
                                                                    src={
                                                                        u.picture
                                                                    }
                                                                    alt={u.name}
                                                                    className="w-8 h-8 rounded-full"
                                                                />
                                                            ) : (
                                                                <span className="text-xs">
                                                                    {u.name
                                                                        .charAt(
                                                                            0
                                                                        )
                                                                        .toUpperCase()}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="font-medium text-sm truncate">
                                                                {u.name}
                                                            </div>
                                                            <div className="text-xs text-base-content/50 truncate">
                                                                {u.email}
                                                            </div>
                                                        </div>
                                                        <span className="badge badge-xs">
                                                            {getUserTypeLabel(
                                                                u.userType
                                                            )}
                                                        </span>
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        className="btn btn-outline btn-sm w-full gap-2"
                                        onClick={() => setShowUserList(true)}
                                    >
                                        <MdSearch className="text-lg" />
                                        Nova conversa privada
                                    </button>
                                )}
                            </div>

                            {/* List */}
                            <div className="flex-1 overflow-y-auto">
                                <ConversationList
                                    conversations={conversations}
                                    currentId={currentConversation?.id}
                                    currentUserId={user?.id || 0}
                                    onSelect={handleSelectConversation}
                                    loading={loading}
                                />
                            </div>
                        </div>

                        {/* Chat window */}
                        <div
                            className={`flex-1 flex flex-col bg-base-100 ${
                                !mobileShowChat && !conversationId
                                    ? 'hidden md:flex'
                                    : ''
                            }`}
                        >
                            {currentConversation ? (
                                <>
                                    <ChatWindow
                                        conversation={currentConversation}
                                        messages={messages}
                                        typingUsers={typingUsers}
                                        currentUserId={user?.id || 0}
                                        onReply={handleReply}
                                        loading={loadingMessages}
                                    />
                                    <MessageInput
                                        conversationId={currentConversation.id}
                                        replyingTo={replyingTo}
                                        onCancelReply={handleCancelReply}
                                        onSend={handleSendMessage}
                                    />
                                </>
                            ) : (
                                <div className="flex-1 flex items-center justify-center text-base-content/60">
                                    <div className="text-center">
                                        <MdPerson className="text-6xl mx-auto mb-4 opacity-30" />
                                        <p>Selecione uma conversa</p>
                                        <p className="text-sm mt-1">
                                            ou inicie uma nova conversa privada
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}

                {/* Broadcast All / AI tabs - show chat directly */}
                {(activeTab === 'all' || activeTab === 'ai') && (
                    <div className="flex-1 flex flex-col bg-base-100">
                        {currentConversation ? (
                            <>
                                <ChatWindow
                                    conversation={currentConversation}
                                    messages={messages}
                                    typingUsers={typingUsers}
                                    currentUserId={user?.id || 0}
                                    onReply={handleReply}
                                    loading={loadingMessages}
                                />
                                <MessageInput
                                    conversationId={currentConversation.id}
                                    replyingTo={replyingTo}
                                    onCancelReply={handleCancelReply}
                                    onSend={handleSendMessage}
                                    hideMediaControls={activeTab === 'ai'}
                                />
                            </>
                        ) : (
                            <div className="flex-1 flex items-center justify-center">
                                <span className="loading loading-spinner loading-lg"></span>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}
